import { useEffect, useState, SyntheticEvent } from 'react';
import { Box, Tab, Tabs, Typography } from '@mui/material';
import { useDispatch } from 'react-redux';
import { useGlobalConfig } from 'context/GlobalContext';
import { useToast } from 'context/ToastContext';
import { RbacApis } from 'apis/rbacAPI';
import { setFeatureGroupsTree } from 'store/dataReducers/rbacSlice';
import { UI_PAGES } from 'framework/access/UiRegistry';
import { usePermissions } from 'hooks/usePermissions';
import { AssignmentsPanel } from '../components/AssignmentsPanel';
import { AccessGroupsPanel } from '../components/AccessGroupsPanel';

type RbacTab = 'assignments' | 'groups';

const ACCESS_CONTROL_CTAS = UI_PAGES.ACCESS_CONTROL.ctas;

export const RbacHomePage = () => {
  const { rc } = useGlobalConfig();
  const toast = useToast();
  const dispatch = useDispatch();
  const { can } = usePermissions();

  const canSeeAssignments = can(ACCESS_CONTROL_CTAS.view_assignments.id);
  const canSeeGroups = can(ACCESS_CONTROL_CTAS.view_access_groups.id);

  const [tab, setTab] = useState<RbacTab>(canSeeAssignments ? 'assignments' : 'groups');

  useEffect(() => {
    (async () => {
      const resp = await RbacApis.getFeatureGroups(rc);
      if (resp.success && resp.data) {
        dispatch(setFeatureGroupsTree(resp.data));
      } else if (resp.error) {
        toast.error(resp.error);
      }
    })();
  }, []);

  const handleTabChange = (_: SyntheticEvent, value: RbacTab) => setTab(value);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Typography variant="h5" sx={{ p: 2, pb: 0.5 }}>Access Control</Typography>
      <Tabs value={tab} onChange={handleTabChange} sx={{ px: 2 }}>
        {canSeeAssignments && <Tab label="Assignments" value="assignments" />}
        {canSeeGroups && <Tab label="Access Groups" value="groups" />}
      </Tabs>
      <Box sx={{ display: 'flex', flexGrow: 1, minHeight: 0, border: 1, borderColor: 'divider', borderRadius: 1, mx: 2, mb: 2 }}>
        {tab === 'assignments' && canSeeAssignments && <AssignmentsPanel />}
        {tab === 'groups' && canSeeGroups && <AccessGroupsPanel />}
      </Box>
    </Box>
  );
};
